import { clamp } from "@/utility/clamp";
import type { ColormapCreate } from "@mm/metx-workbench.meteomatics.com";
import { forwardRef, useId } from "react";
import type { CSSProperties } from "react";
import "./CustomColormapLegend.scss";
import { normalizeColormap } from "./utils/colormap";
import type { LegendColormap } from "./utils/colormap";
import { formatTick } from "./utils/ticks";

const COLORMAP_TYPE: Record<"CONTINUOUS" | "SEGMENTED", LegendColormap["type"]> = {
  CONTINUOUS: "continuous",
  SEGMENTED: "segmented",
};

const LEGEND_METRICS = {
  defaultBarWidth: 18,
  defaultBarHeight: 220,
  margin: { top: 0, right: 40, bottom: 0, left: 20 },
  tickLength: 8,
  labelOffset: 12,
  titleGap: 14,
} as const;

const FONT = {
  titleSize: 10,
  titleWeight: 600,
  labelSize: 10,
  labelWeight: 500,
  letterSpacing: 0.2,
} as const;

const SEGMENT_STROKE = {
  thin: 0.6,
  normal: 0.8,
  outline: 0.9,
} as const;

const SEGMENT_THRESHOLD = {
  stroke: 10,
} as const;

const PANEL_PADDING = {
  x: 10,
  y: 0,
} as const;

const TICK_TEXT_OFFSET_Y = 4;
const MIN_TICK_LABEL_GAP = FONT.labelSize + 4;

type LegendSvgProps = {
  colormap: ColormapCreate;
  barWidth?: number;
  barHeight?: number;
  title?: string;
  className?: string;
  style?: CSSProperties;
  resizable?: boolean;
  unit?: string;
  panelPadding?: { x: number; y: number };
};

export const CustomColormapLegend = forwardRef<SVGSVGElement, LegendSvgProps>(
  (
    {
      colormap,
      barWidth = LEGEND_METRICS.defaultBarWidth,
      barHeight = LEGEND_METRICS.defaultBarHeight,
      title,
      className,
      style,
      resizable = false,
      unit,
      panelPadding = PANEL_PADDING,
    },
    ref,
  ) => {
    const gradientId = `legend-gradient-${useId().replace(/:/g, "")}`;
    let normalizedColormap: LegendColormap;
    try {
      normalizedColormap = normalizeColormap(colormap);
    } catch (error) {
      console.error("Invalid colormap passed to CustomColormapLegend:", error);
      return null;
    }
    const { type, stops, label } = normalizedColormap;

    const margin = LEGEND_METRICS.margin;
    const tickLength = LEGEND_METRICS.tickLength;
    const labelOffset = LEGEND_METRICS.labelOffset;
    const legendTitle = title ?? label ?? null;
    const titleColumnWidth = legendTitle ? FONT.titleSize + LEGEND_METRICS.titleGap : 0;

    const values = stops.map((stop) => stop.value);
    const min = values[0];
    const max = values[values.length - 1];
    const span = Math.max(1e-6, max - min);

    const barX = margin.left + titleColumnWidth;
    const barY = margin.top + panelPadding.y;
    const viewBoxWidth = margin.left + titleColumnWidth + barWidth + tickLength + labelOffset + margin.right;
    const viewBoxHeight = barY + barHeight + margin.bottom + panelPadding.y;

    const valueToY = (value: number) => barY + ((max - clamp(value, min, max)) / span) * barHeight;

    const tickSet = new Set<number>();
    for (const stop of stops) {
      tickSet.add(Number(stop.value.toFixed(6)));
    }
    const ticks = Array.from(tickSet).sort((a, b) => a - b);

    const labeledTicks = (() => {
      if (ticks.length <= 2) {
        return new Set(ticks);
      }

      const ticksWithY = ticks
        .map((tick) => ({ tick, y: valueToY(tick) + TICK_TEXT_OFFSET_Y }))
        .sort((a, b) => a.y - b.y);

      const first = ticksWithY[0];
      const last = ticksWithY[ticksWithY.length - 1];

      const selected: Array<{ tick: number; y: number }> = [first];
      for (const candidate of ticksWithY.slice(1, -1)) {
        const prev = selected[selected.length - 1];
        if (candidate.y - prev.y >= MIN_TICK_LABEL_GAP) {
          selected.push(candidate);
        }
      }

      const prev = selected[selected.length - 1];
      if (last.y - prev.y < MIN_TICK_LABEL_GAP && selected.length > 1) {
        selected.pop();
      }
      selected.push(last);

      return new Set(selected.map((entry) => entry.tick));
    })();

    const stopsWithOffset = stops
      .map((stop) => ({
        stop,
        offset: ((stop.value - min) / span) * 100,
      }))
      .sort((a, b) => a.offset - b.offset);

    const segmentedRects =
      type === COLORMAP_TYPE.SEGMENTED
        ? stops.slice(0, -1).map((stop, index) => {
            const next = stops[index + 1];
            const yTop = valueToY(next.value);
            const yBottom = valueToY(stop.value);
            const height = yBottom - yTop;
            return {
              key: `${index}-${stop.value}-${next.value}`,
              y: yTop,
              height,
              fill: stop.color,
              strokeWidth: height < SEGMENT_THRESHOLD.stroke ? SEGMENT_STROKE.thin : SEGMENT_STROKE.normal,
            };
          })
        : [];

    const tickStep = ticks[1] ? ticks[1] - ticks[0] : span;
    const tickRows = ticks.map((tick) => {
      const y = valueToY(tick);
      const label = labeledTicks.has(tick) ? `${formatTick(tick, tickStep)}${unit ? ` ${unit}` : ""}` : "";
      return { tick, y, label };
    });

    const legendSvg = (
      <svg
        ref={ref}
        width={resizable ? "100%" : viewBoxWidth}
        height={resizable ? "100%" : viewBoxHeight}
        viewBox={`0 0 ${viewBoxWidth} ${viewBoxHeight}`}
        role="img"
        aria-label="Color legend"
        xmlns="http://www.w3.org/2000/svg"
        className={["custom-colormap-legend", className].filter(Boolean).join(" ")}
        style={style}
        preserveAspectRatio="xMidYMid meet"
      >
        <defs>
          {type === COLORMAP_TYPE.CONTINUOUS && (
            <linearGradient
              id={gradientId}
              gradientUnits="userSpaceOnUse"
              x1={barX}
              y1={barY + barHeight}
              x2={barX}
              y2={barY}
            >
              {stopsWithOffset.map(({ stop, offset }, index) => {
                return <stop key={`${stop.value}-${index}`} offset={`${offset}%`} stopColor={stop.color} />;
              })}
            </linearGradient>
          )}
        </defs>

        {legendTitle ? (
          <text
            x={barX - LEGEND_METRICS.titleGap}
            y={barY + barHeight / 2}
            transform={`rotate(-90 ${barX - LEGEND_METRICS.titleGap} ${barY + barHeight / 2})`}
            textAnchor="middle"
            dominantBaseline="middle"
            fontSize={FONT.titleSize}
            fontWeight={FONT.titleWeight}
            letterSpacing={FONT.letterSpacing}
            className="custom-colormap-legend__title"
          >
            {legendTitle}
          </text>
        ) : null}

        {type === COLORMAP_TYPE.CONTINUOUS ? (
          <rect
            x={barX}
            y={barY}
            width={barWidth}
            height={barHeight}
            fill={`url(#${gradientId})`}
            strokeWidth={SEGMENT_STROKE.normal}
            className="custom-colormap-legend__bar"
          />
        ) : (
          <g>
            {segmentedRects.map((rect) => (
              <rect
                key={rect.key}
                x={barX}
                y={rect.y}
                width={barWidth}
                height={rect.height}
                fill={rect.fill}
                strokeWidth={rect.strokeWidth}
                rx={0}
                className="custom-colormap-legend__segment"
              />
            ))}
            <rect
              x={barX}
              y={barY}
              width={barWidth}
              height={barHeight}
              fill="none"
              strokeWidth={SEGMENT_STROKE.outline}
              className="custom-colormap-legend__outline"
            />
          </g>
        )}

        <g fontSize={FONT.labelSize} fontWeight={FONT.labelWeight}>
          {tickRows.map(({ tick, y, label }) => {
            return (
              <g key={tick} transform={`translate(0 ${y})`}>
                <line
                  x1={barX + barWidth}
                  x2={barX + barWidth + tickLength}
                  y1={0}
                  y2={0}
                  strokeWidth={SEGMENT_STROKE.normal}
                  className="custom-colormap-legend__tick-line"
                />
                <text
                  x={barX + barWidth + tickLength + labelOffset}
                  y={TICK_TEXT_OFFSET_Y}
                  textAnchor="start"
                  fontSize={FONT.labelSize}
                  className="custom-colormap-legend__tick-label"
                >
                  {label}
                </text>
              </g>
            );
          })}
        </g>
      </svg>
    );

    if (resizable) {
      return (
        <div
          className="custom-colormap-legend__resizable"
          style={{
            width: viewBoxWidth,
            height: viewBoxHeight,
          }}
        >
          {legendSvg}
        </div>
      );
    }

    return legendSvg;
  },
);
